import { Stack, TextField, Typography } from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import { Ref, useImperativeHandle, forwardRef } from 'react';
import * as yup from 'yup';

import { CountrySelect } from '../common/CountrySelect';

import { Property, resolveCountryCodeOrDefault } from '@/models/Property';
import { useTranslationPrefix } from '@/translations/i18n';

export type EditPropertyFormValues = {
  propertyName: string;
};

export type EditPropertyFormRef<T extends EditPropertyFormValues> = FormikProps<T>;

export interface EditPropertyFormProps<T extends EditPropertyFormValues> {
  /**
   * The reminder to be edited. Should not be provided when creating a reminder.
   */
  property: Property;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: (values: T) => void;
}

const EditPropertyFormComponent = <T extends EditPropertyFormValues>(
  { property, onSubmit }: EditPropertyFormProps<T>,
  ref: Ref<EditPropertyFormRef<T>>,
) => {
  const t = useTranslationPrefix('edit_property');
  const validationT = useTranslationPrefix('validation');

  const validationSchema = yup.object({
    propertyName: yup
      .string()
      .required(validationT('required', { name: t('field.property_name') })),
  });

  const formik = useFormik<T>({
    initialValues: {
      propertyName: property.name,
    } as T,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit,
  });

  useImperativeHandle(ref, () => formik);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={5}>
        <Stack spacing={2}>
          <Typography component="label" htmlFor="property-name-field" variant="body3">
            {t('field.property_name')}
          </Typography>
          <TextField
            id="property-name-field"
            name="propertyName"
            aria-label={t('field.property_name')}
            fullWidth
            required
            autoFocus
            value={formik.values.propertyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.propertyName && Boolean(formik.errors.propertyName)}
            helperText={formik.touched.propertyName && (formik.errors.propertyName as string)}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography component="label" htmlFor="property-country-field" variant="body3">
            {t('field.property_country')}
          </Typography>
          <CountrySelect
            id="property-country-field"
            name="propertyCountry"
            aria-label={t('field.property_country')}
            fullWidth
            disabled
            value={resolveCountryCodeOrDefault(property)}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography component="label" htmlFor="property-address-field" variant="body3">
            {t('field.property_address')}
          </Typography>
          <TextField
            id="property-address-field"
            name="propertyAddress"
            aria-label={t('field.property_address')}
            fullWidth
            disabled
            value={property.address}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography component="label" htmlFor="property-city-field" variant="body3">
            {t('field.property_city')}
          </Typography>
          <TextField
            id="property-city-field"
            name="propertyCity"
            aria-label={t('field.property_city')}
            fullWidth
            disabled
            value={property.city}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography component="label" htmlFor="property-state-field" variant="body3">
            {t('field.property_state')}
          </Typography>
          <TextField
            id="property-state-field"
            name="propertyState"
            aria-label={t('field.property_state')}
            fullWidth
            disabled
            value={property.state}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography component="label" htmlFor="property-zipcode-field" variant="body3">
            {t('field.property_zip_code')}
          </Typography>
          <TextField
            id="property-zipcode-field"
            name="propertyZipCode"
            aria-label={t('field.property_zip_code')}
            fullWidth
            disabled
            value={property.zipCode}
          />
        </Stack>
      </Stack>
    </form>
  );
};

export const EditPropertyForm = forwardRef(EditPropertyFormComponent) as <
  T extends EditPropertyFormValues,
>(
  props: EditPropertyFormProps<T> & { ref?: Ref<EditPropertyFormRef<T>> },
) => ReturnType<typeof EditPropertyFormComponent<T>>;
